<script setup>
import { ref, computed, onMounted } from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const emit = defineEmits(['import']);
const { t } = useI18n();
const store = useStore();

const uiFlags = useMapGetter('contacts/getUIFlags');
const isImportingContact = computed(() => uiFlags.value.isImporting);

const dialogRef = ref(null);
const fileInput = ref(null);

const hasSelectedFile = ref(null);
const selectedFileName = ref('');

const csvUrl = '/downloads/import-contacts-sample.csv';

// Optional per-batch options: a label applied to every imported contact + the
// conversation created for it (if a kanban destination is picked), and where to
// drop that conversation's card. See contacts_controller#import / DataImportJob.
const accountLabels = useMapGetter('labels/getLabels');
const inboxes = useMapGetter('inboxes/getInboxes');
const myKanbans = useMapGetter('kanbans/getMyKanbans');
const stagesGetter = useMapGetter('kanbans/getStagesByKanbanId');

const selectedLabel = ref('');
const selectedInboxId = ref('');
const selectedKanbanId = ref('');
const selectedStageId = ref('');

const stagesForSelectedKanban = computed(() =>
  selectedKanbanId.value ? stagesGetter.value(selectedKanbanId.value) : []
);

onMounted(() => {
  if (!accountLabels.value.length) store.dispatch('labels/get');
  if (!inboxes.value.length) store.dispatch('inboxes/get');
  if (!myKanbans.value.length) store.dispatch('kanbans/get');
});

const handleFileClick = () => fileInput.value?.click();

const processFileName = fileName => {
  const lastDotIndex = fileName.lastIndexOf('.');
  const extension = fileName.slice(lastDotIndex);
  const baseName = fileName.slice(0, lastDotIndex);

  return baseName.length > 20
    ? `${baseName.slice(0, 20)}...${extension}`
    : fileName;
};

const handleFileChange = () => {
  const file = fileInput.value?.files[0];
  hasSelectedFile.value = file;
  selectedFileName.value = file ? processFileName(file.name) : '';
};

const handleRemoveFile = () => {
  hasSelectedFile.value = null;
  if (fileInput.value) {
    fileInput.value.value = null;
  }
  selectedFileName.value = '';
};

// A kanban target only makes sense with both a stage and an inbox picked — a stage
// alone has nowhere to attach the conversation to. Silently drop a half-filled
// selection rather than sending a request the backend would reject.
const hasCompleteKanbanTarget = computed(
  () => !!(selectedStageId.value && selectedInboxId.value)
);

const uploadFile = async () => {
  if (!hasSelectedFile.value) return;
  emit('import', {
    file: hasSelectedFile.value,
    labelTitle: selectedLabel.value || undefined,
    targetInboxId: hasCompleteKanbanTarget.value
      ? selectedInboxId.value
      : undefined,
    targetKanbanStageId: hasCompleteKanbanTarget.value
      ? selectedStageId.value
      : undefined,
  });
};

defineExpose({ dialogRef });
</script>

<template>
  <Dialog
    ref="dialogRef"
    :title="t('CONTACTS_LAYOUT.HEADER.ACTIONS.IMPORT_CONTACT.TITLE')"
    :confirm-button-label="
      t('CONTACTS_LAYOUT.HEADER.ACTIONS.IMPORT_CONTACT.IMPORT')
    "
    :is-loading="isImportingContact"
    :disable-confirm-button="isImportingContact"
    @confirm="uploadFile"
  >
    <template #description>
      <p class="mb-0 text-sm text-n-slate-11">
        {{ t('CONTACTS_LAYOUT.HEADER.ACTIONS.IMPORT_CONTACT.DESCRIPTION') }}
        <a
          :href="csvUrl"
          target="_blank"
          rel="noopener noreferrer"
          download="import-contacts-sample.csv"
          class="text-n-blue-11"
        >
          {{
            t('CONTACTS_LAYOUT.HEADER.ACTIONS.IMPORT_CONTACT.DOWNLOAD_LABEL')
          }}
        </a>
      </p>
    </template>

    <div class="flex flex-col gap-2">
      <div class="flex items-center gap-2">
        <label class="text-sm text-n-slate-12 whitespace-nowrap">
          {{ t('CONTACTS_LAYOUT.HEADER.ACTIONS.IMPORT_CONTACT.LABEL') }}
        </label>
        <div class="flex items-center justify-between w-full gap-2">
          <span v-if="hasSelectedFile" class="text-sm text-n-slate-12">
            {{ selectedFileName }}
          </span>
          <Button
            v-if="!hasSelectedFile"
            :label="
              t('CONTACTS_LAYOUT.HEADER.ACTIONS.IMPORT_CONTACT.CHOOSE_FILE')
            "
            icon="i-lucide-upload"
            color="slate"
            variant="ghost"
            size="sm"
            class="!w-fit"
            @click="handleFileClick"
          />
          <div v-else class="flex items-center gap-1">
            <Button
              :label="t('CONTACTS_LAYOUT.HEADER.ACTIONS.IMPORT_CONTACT.CHANGE')"
              color="slate"
              variant="ghost"
              size="sm"
              @click="handleFileClick"
            />
            <div class="w-px h-3 bg-n-strong" />
            <Button
              icon="i-lucide-trash"
              color="slate"
              variant="ghost"
              size="sm"
              @click="handleRemoveFile"
            />
          </div>
        </div>
      </div>

      <div class="flex flex-col gap-1">
        <label class="text-sm text-n-slate-12">
          {{ t('CONTACTS_LAYOUT.HEADER.ACTIONS.IMPORT_CONTACT.APPLY_LABEL') }}
        </label>
        <select v-model="selectedLabel" class="reply-composer--input">
          <option value="">
            {{ t('CONTACTS_LAYOUT.HEADER.ACTIONS.IMPORT_CONTACT.NO_LABEL') }}
          </option>
          <option
            v-for="label in accountLabels"
            :key="label.id"
            :value="label.title"
          >
            {{ label.title }}
          </option>
        </select>
      </div>

      <div class="flex flex-col gap-1">
        <label class="text-sm text-n-slate-12">
          {{
            t('CONTACTS_LAYOUT.HEADER.ACTIONS.IMPORT_CONTACT.CREATE_KANBAN_CARDS')
          }}
        </label>
        <div class="flex items-center gap-2">
          <select v-model="selectedKanbanId" class="reply-composer--input flex-1">
            <option value="">
              {{ t('CONTACTS_LAYOUT.HEADER.ACTIONS.IMPORT_CONTACT.NO_KANBAN') }}
            </option>
            <option
              v-for="kanban in myKanbans"
              :key="kanban.id"
              :value="kanban.id"
            >
              {{ kanban.name }}
            </option>
          </select>
          <select
            v-if="selectedKanbanId"
            v-model="selectedStageId"
            class="reply-composer--input flex-1"
          >
            <option value="">
              {{
                t('CONTACTS_LAYOUT.HEADER.ACTIONS.IMPORT_CONTACT.SELECT_STAGE')
              }}
            </option>
            <option
              v-for="stage in stagesForSelectedKanban"
              :key="stage.id"
              :value="stage.id"
            >
              {{ stage.name }}
            </option>
          </select>
        </div>
        <select
          v-if="selectedKanbanId && selectedStageId"
          v-model="selectedInboxId"
          class="reply-composer--input"
        >
          <option value="">
            {{
              t('CONTACTS_LAYOUT.HEADER.ACTIONS.IMPORT_CONTACT.SELECT_INBOX')
            }}
          </option>
          <option v-for="inbox in inboxes" :key="inbox.id" :value="inbox.id">
            {{ inbox.name }}
          </option>
        </select>
      </div>
    </div>
    <input
      ref="fileInput"
      type="file"
      accept="text/csv"
      class="hidden"
      @change="handleFileChange"
    />
  </Dialog>
</template>
